import { Box, Button, TablePagination, Tooltip } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { TickCircle } from 'iconsax-react';
import type { FC, ReactNode } from 'react';

import { useSelectedItems } from '@core/hooks/useSelectedItems';
import type { NotificationMessage } from '@core/types/api/NotificationMessage/NotificationMessage';
import { hasValue } from '@core/utils/equality';
import { type NotificationCardAction, notificationCardItem } from '@core/utils/notificationCard';
import {
  type NotificationFilterValues,
  type NotificationSortOption,
  UNREAD_FILTER,
  notificationFilters,
  notificationSort,
} from '@core/utils/notificationQueryParams';
import type { NotificationTab } from '@core/utils/notifications';
import { useEmptyPageGuard, usePagination } from '@web/hooks/usePagination';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage, useSlabQuery } from '@web/hooks/useSlabQuery';
import { useConfirmDialog } from '@web/providers/ConfirmProvider/useConfirmDialog';
import { useNotificationPanel } from '@web/providers/NotificationPanelProvider/NotificationPanelProvider';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { SkeletonList } from '../SkeletonList/SkeletonList';
import { NotificationCard, NotificationCardSkeleton } from './NotificationCard';
import {
  NotificationCenterEmptyState,
  notificationEmptyStateText,
} from './NotificationCenterEmptyState';
import { NotificationsBulkActions } from './NotificationsBulkActions';

type Props = {
  tab: NotificationTab;
  search: string;
  filterValues: NotificationFilterValues;
  sortOption: NotificationSortOption;
};

export const NotificationsList: FC<Props> = ({ tab, search, filterValues, sortOption }) => {
  const { scope, scopeObject, pendingArrivalIds, markArrivalShown } = useNotificationPanel();

  const scopeObjectId = hasValue(scopeObject) ? scopeObject.id : null;

  const { page, setPage, perPage } = usePagination({
    resetOn: [tab, search, scope, scopeObjectId, filterValues, sortOption],
    initialPerPage: 25,
  });

  const { data, isPending, isFetching } = useSlabQuery(
    'GET notifications',
    {
      queryParams: {
        perPage,
        page,
        filterBy: notificationFilters({ tab, search, scope, scopeObject, filterValues }),
        sortBy: notificationSort(sortOption),
      },
    },
    { placeholderData: keepPreviousData },
  );

  const notifications = data?.items ?? [];
  const totalCount = data?.count ?? 0;

  const { data: unread } = useSlabQuery('GET notifications', {
    queryParams: { perPage: 1, page: 0, filterBy: [UNREAD_FILTER] },
  });

  const confirm = useConfirmDialog();
  const toast = useToast({ subjectName: 'Notification' });

  const { mutate: updateReadStatus } = useSlabMutation('POST bulk notifications read status', {
    onSuccess: (_, { args }) => {
      const count = args.body.ids.length;
      toast.success({
        message:
          count > 1 ? `${count} Notifications marked as read` : 'Notification marked as read',
      });
    },
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error, 'Failed to mark as read') });
    },
  });
  const { mutate: markAllAsRead } = useSlabMutation('POST mark all notifications as read', {
    onSuccess: () => {
      toast.success({ message: 'All notifications marked as read' });
    },
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error, 'Failed to mark all as read') });
    },
  });
  const { mutate: archiveNotifications } = useSlabMutation('POST bulk archive notifications', {
    onSuccess: (_, { args }) => {
      const count = args.body.ids.length;
      toast.success({
        message: count > 1 ? `${count} Notifications deleted` : 'Notification deleted',
      });
    },
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error, 'Failed to delete') });
    },
  });

  useEmptyPageGuard({ page, setPage, itemCount: notifications.length, isFetching });

  const { selectedKeys, isSelected, isAllSelected, setSelected, setAllSelected, clearSelection } =
    useSelectedItems({
      keys: notifications.map((notification) => notification.id),
      resetOn: [tab, search, scope, scopeObjectId, filterValues, page],
    });

  const hasUnreadSelected = notifications.some(
    (notification) => !notification.isRead && selectedKeys.includes(notification.id),
  );

  const markAsRead = (ids: string[], onMarked?: () => void): void => {
    const filteredIds = ids.filter(
      (id) => !notifications.find((notification) => notification.id === id)?.isRead,
    );
    updateReadStatus(
      { args: { body: { ids: filteredIds, isRead: true } } },
      { onSuccess: onMarked },
    );
  };

  const confirmArchive = (ids: string[], onArchived?: () => void): void => {
    const subjectName = ids.length > 1 ? `these ${ids.length} notifications` : 'this notification';
    confirm.alert({
      title: 'Confirm Delete',
      description: `Are you sure you want to delete ${subjectName}?`,
      actions: [
        { label: 'Cancel', variant: 'slab-primary' },
        {
          label: 'Delete',
          variant: 'slab-secondary',
          onClick: (): void => {
            archiveNotifications({ args: { body: { ids } } }, { onSuccess: onArchived });
          },
        },
      ],
    });
  };

  const handlePressAction = (
    action: NotificationCardAction,
    notification: NotificationMessage,
  ): void => {
    if (action === 'markAsRead') {
      markAsRead([notification.id]);
    } else if (action === 'delete') {
      confirmArchive([notification.id]);
    }
  };

  const handleBulkMarkAsRead = (): void => markAsRead(selectedKeys, clearSelection);

  const handleBulkDelete = (): void => confirmArchive(selectedKeys, clearSelection);

  const handleMarkAllAsRead = (): void => {
    confirm.alert({
      title: 'Confirm Mark All Read',
      description: `Are you sure you want to mark all (${unread?.count ?? 0}) notifications as read?`,
      actions: [
        { label: 'Cancel', variant: 'slab-secondary' },
        {
          label: 'Mark All Read',
          variant: 'slab-primary',
          onClick: (): void => {
            markAllAsRead({ args: {} }, { onSuccess: clearSelection });
          },
        },
      ],
    });
  };

  const content = ((): ReactNode => {
    if (isPending) {
      return (
        <SkeletonList>
          <NotificationCardSkeleton />
        </SkeletonList>
      );
    }

    if (notifications.length === 0) {
      return (
        <NotificationCenterEmptyState
          {...notificationEmptyStateText(tab, { search, scope, scopeObject })}
        />
      );
    }

    return notifications.map((notification) => {
      return (
        <NotificationCard
          key={notification.id}
          item={notificationCardItem(notification)}
          shouldAnimateArrival={pendingArrivalIds.has(notification.id)}
          onArrivalShown={(): void => markArrivalShown(notification.id)}
          onAction={(action) => handlePressAction(action, notification)}
          isSelected={isSelected(notification.id)}
          onSelectedChange={(isChecked): void => setSelected(notification.id, isChecked)}
        />
      );
    });
  })();

  const actions = ((): ReactNode => {
    if (selectedKeys.length > 0) {
      return (
        <NotificationsBulkActions
          selectedCount={selectedKeys.length}
          isAllSelected={isAllSelected}
          onSelectAllChange={setAllSelected}
          onMarkAsRead={
            selectedKeys.length > 1 && hasUnreadSelected ? handleBulkMarkAsRead : undefined
          }
          onDelete={selectedKeys.length > 1 ? handleBulkDelete : undefined}
          onClear={clearSelection}
        />
      );
    }

    if (notifications.length === 0) {
      return null;
    }

    return (
      <Box
        display='flex'
        alignItems='center'
        justifyContent='flex-end'
        marginX={2}
        marginY='12px'
        height={54}
        flexShrink={0}
      >
        <Tooltip
          title={
            unread?.count
              ? `Mark all (${unread?.count}) notifications as read`
              : 'No unread notifications'
          }
        >
          <Box>
            <Button
              variant='slab-action-plain'
              startIcon={<TickCircle variant='TwoTone' />}
              disabled={(unread?.count ?? 0) === 0}
              onClick={handleMarkAllAsRead}
            >
              Mark All Read
            </Button>
          </Box>
        </Tooltip>
      </Box>
    );
  })();

  return (
    <Box display='flex' flexDirection='column' flex={1} minHeight={0}>
      <Box>{actions}</Box>
      <Box
        flex={1}
        minHeight={0}
        overflow='auto'
        paddingBottom={2}
        display='flex'
        flexDirection='column'
      >
        {content}
      </Box>

      {totalCount > perPage && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='flex-end'
          gap={1}
          paddingX={1}
          borderTop='1px solid'
          borderColor='SlabGray.Stroke'
        >
          {isFetching && !isPending && <LoadingSpinner size={16} wrapInBox={false} />}
          <TablePagination
            component='div'
            rowsPerPageOptions={[]}
            count={totalCount}
            rowsPerPage={perPage}
            page={page}
            onPageChange={(_, nextPage): void => setPage(nextPage)}
            labelDisplayedRows={({ from, to, count }): string => `${from}–${to} of ${count}`}
            sx={{
              '> .MuiToolbar-root': { minHeight: 0, paddingLeft: 0 },
              '& p': { fontSize: 'inherit', lineHeight: 1, margin: 0 },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
